import { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Analytics } from "@vercel/analytics/react";

import Typewriter from "typewriter-effect";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import ProjectCard from "./ProjectCard";
import { classes } from "./classes";
import { projects } from "./projects";
import { fetchLastCommitDate } from "./fetchCommit";
import Form from "./Form";

import "./style.css";

const App = () => {
  const [selectedTab, setSelectedTab] = useState("intro");
  const [fade, setFade] = useState("fade-in");
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    const getLastCommitDate = async () => {
      const date = await fetchLastCommitDate();
      if (date) {
        setLastUpdated(date);
      }
    };

    getLastCommitDate();
  }, []);

  useEffect(() => {
    setFade("fade-in");
  }, [selectedTab]);

  const handleTabClick = (tab) => {
    if (tab !== selectedTab) {
      setFade("fade-out");
      setTimeout(() => {
        setSelectedTab(tab);
        setFade("fade-in");
      }, 500);
    }
  };

  return (
    <div>
      <header>
        <h1>colin henderson</h1>
        <div className="header-icons">
          <a
            className="linkButton"
            href="https://github.com/0xcolinhenderson/"
            target="_blank"
          >
            <img src="/github.svg" alt="github-icon" />
          </a>
          <a
            className="linkButton"
            href="https://linkedin.com/in/colinchenderson/"
            target="_blank"
          >
            <img src="/linkedin.svg" alt="linkedin-icon" />
          </a>
          <a
            className="linkButton"
            href="mailto:colin.ch.henderson@gmail.com"
            target="_blank"
          >
            <img src="/email.svg" alt="email-icon" />
          </a>
          <img id="location" src="/location.svg" alt="location-icon" />
          <p id="location-text">santa cruz, ca</p>
        </div>
        <h2 className="updated-text">[ last updated: 6/16/2026 ]</h2>
      </header>
      <div id="content">
        <section id="intro">
          <h2 className="title">
            <Typewriter
              options={{
                autoStart: true,
                loop: false,
                delay: 75,
              }}
              onInit={(typewriter) => {
                typewriter
                  .typeString(
                    '<span style="font-weight: normal; color: var(--main-color);">hi!</span>'
                  )
                  .pauseFor(2000)
                  .typeString(" i'm colin henderson")
                  .start();
              }}
            />
          </h2>
        </section>
        <div className="menu-container">
          <div className="menu">
            <section id="intro" onClick={() => handleTabClick("intro")}>
              <h3
                className={`sectionHeader hover-effect ${
                  selectedTab === "intro" ? "active" : ""
                }`}
              >
                intro
              </h3>
            </section>
            <section id="projects" onClick={() => handleTabClick("projects")}>
              <h3
                className={`sectionHeader hover-effect ${
                  selectedTab === "projects" ? "active" : ""
                }`}
              >
                projects
              </h3>
            </section>
            <section id="resume" onClick={() => handleTabClick("resume")}>
              <h3
                className={`sectionHeader hover-effect ${
                  selectedTab === "resume" ? "active" : ""
                }`}
              >
                resume
              </h3>
            </section>
          </div>
          <div className={`displayedContent ${fade}`}>
            {selectedTab === "intro" && (
              <>
                <p>
                  I'm a recent graduate from{" "}
                  <a
                    className="link"
                    target="_blank"
                    href="https://engineering.ucsc.edu/"
                  >
                    UC Santa Cruz
                  </a>{" "}
                  with a bachelor's in Computer Science.
                </p>
                <p>
                  I'm always actively looking for opportunities to learn and
                  grow as a developer. I have experience creating projects with
                  a variety of languages and frameworks, including <a>C#</a>,
                  <a>Python</a>, <a>JavaScript</a>, and <a>C++</a>.
                </p>
                <p>
                  I also have industry experience as a backend engineer intern,
                  where I primarily worked with <a>C#</a> in the .NET
                  ecosystem.
                </p>
                <p>
                  I'm a really big fan of .NET, and in my free time I love making games in Unity.
                </p>
                <p>
                  Outside of programming, I'm super into music production. A majority of my personal projects have been tools to make my music production process easier.
                </p>
                <p className="subtitle">
                  If you're interested in seeing some of my work, please check
                  out the{" "}
                  <span
                    className="highlight"
                    id="projects"
                    onClick={() => handleTabClick("projects")}
                  >
                    projects{" "}
                  </span>
                  tab.
                </p>
              </>
            )}
            {selectedTab === "projects" && (
              <div className="projects-grid">
                {projects.map((project, index) => (
                  <ProjectCard
                    key={index}
                    image={project.image}
                    name={project.name}
                    subtitle={project.subtitle}
                    languages={project.languages}
                    webLink={project.webLink}
                    githubLink={project.githubLink}
                  />
                ))}
              </div>
            )}
            {selectedTab === "resume" && (
              <>
                <p className="subtitle">
                  If you're on mobile, you can download it{" "}
                  <a className="link" href="/resume.pdf" download>
                    here
                  </a>
                  .
                </p>
                <embed className="resume" src="/resume.pdf" />
              </>
            )}
          </div>
        </div>
      </div>
      <Analytics />
    </div>
  );
};

const root = ReactDOM.createRoot(document.getElementById("app"));
root.render(<App />);
